import React, { useState, useEffect, useRef } from 'react';
import { Home, FileBarChart, Menu, X, User, LogOut, LogIn, Settings, Users, Gavel, Zap, BookOpen, HelpCircle, Star, ArrowUpRight, Flame, BellRing, FolderOpen, Rss, Sparkles, ChevronDown } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { userTierService } from '../lib/userTierService';
import { canUsePublicFiles } from '../lib/publicFiles';
import { useFirm } from '../hooks/useFirm';
import { Logo } from './common/Logo';

interface NavigationProps {
  userEmail?: string;
  onSignOut: () => void;
  isAnonymous?: boolean;
  onShowAuth?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ userEmail, onSignOut, isAnonymous = false, onShowAuth }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userTier, setUserTier] = useState<string>('');
  const [tierError, setTierError] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const [commentSummariesUsage, setCommentSummariesUsage] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Fetch user tier and usage when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAnonymous && userEmail) {
        try {
          setLoading(true);
          setTierError(false);
          // user_tiers.tier is CHECK-constrained to anonymous|free|paid, and the
          // webhook writes 'paid' for every self-serve Pro subscriber. This nav's
          // access checks / badge / usage widget are all written against 'pro',
          // so normalize here — otherwise a paying customer sees "Free Plan" and
          // gets bounced to /pricing on every premium click.
          const rawTier = await userTierService.getUserTier();
          const tier = rawTier === 'paid' ? 'pro' : rawTier;
          setUserTier(tier);

          // Fetch usage data
          const usage = await userTierService.getUserUsage();
          if (usage) {
            setCommentSummariesUsage(usage.comment_summaries_count || 0);
          }
        } catch (error) {
          // Don't claim 'free' on failure — that would show a paying user the
          // Free badge + Upgrade nudge. Flag the error and render a neutral state.
          console.error('Error fetching user data:', error);
          setTierError(true);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [isAnonymous, userEmail]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleAccountDropdown = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  const { isFirmMember } = useFirm();

  // Mobile sidebar items. Must stay at parity with the desktop nav — it
  // previously omitted Comment Summaries and Firm console, leaving phone/tablet
  // users (e.g. a firm attorney) with no way to reach their own console except
  // by typing the URL.
  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/proceedings', label: 'Proceedings', icon: Gavel },
    { path: '/watchlist', label: 'Watchlist', icon: BellRing },
    { path: '/trending', label: 'Trending', icon: Flame },
    { path: '/comment-summaries', label: 'Comment Summaries', icon: Sparkles },
    { path: '/library', label: 'Library', icon: FileBarChart },
    { path: '/workspaces', label: 'Workspaces', icon: Users },
    ...(isFirmMember ? [{ path: '/firm', label: 'Firm console', icon: Star }] : []),
    { path: '/public-files', label: 'Public Files', icon: FolderOpen },
    { path: '/docs', label: 'Documentation', icon: BookOpen },
  ].filter((item) => item.path !== '/public-files' || canUsePublicFiles(userEmail));

  const closeSidebar = () => setSidebarOpen(false);

  const isSummariesTab =
    location.pathname === '/library' &&
    new URLSearchParams(location.search).get('tab') === 'summaries';
  const isLibraryRoute = location.pathname === '/library';

  // Hoverable Library menu state. Open on mouse-enter, close on
  // mouse-leave with a short delay so the user can travel from the
  // trigger to the dropdown without it flickering shut.
  const [libraryMenuOpen, setLibraryMenuOpen] = useState(false);
  const libraryMenuTimerRef = useRef<number | null>(null);
  const openLibraryMenu = () => {
    if (libraryMenuTimerRef.current) {
      window.clearTimeout(libraryMenuTimerRef.current);
      libraryMenuTimerRef.current = null;
    }
    setLibraryMenuOpen(true);
  };
  const scheduleCloseLibraryMenu = () => {
    if (libraryMenuTimerRef.current) {
      window.clearTimeout(libraryMenuTimerRef.current);
    }
    libraryMenuTimerRef.current = window.setTimeout(() => {
      setLibraryMenuOpen(false);
    }, 120);
  };

  // Firm attorneys get a dedicated console — strip the consumer nav + plan
  // badge while they're inside /firm so it reads as their own tool.
  const onFirm = location.pathname.startsWith('/firm');

  const topLinkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3 py-1.5 text-[13px] font-normal transition-colors ${
      isActive ? 'text-white' : 'text-white/55 hover:bg-white/[0.06] hover:text-white/90'
    }`;

  // Format tier name for display
  const getTierDisplayName = (tier: string): string => {
    switch (tier) {
      case 'free':
        return 'Free Plan';
      case 'prosumer':
        return 'Prosumer Plan';
      case 'pro':
        return 'Pro Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      case 'anonymous':
        return 'Anonymous';
      default:
        return 'Free Plan';
    }
  };

  const avatarInitial =
    userEmail && userEmail.length > 0
      ? userEmail.trim().charAt(0).toUpperCase()
      : '?';

  // Metered comment-summary allowance for the CURRENT user (free = 2/mo,
  // prosumer = 5/mo), from the single source of truth (userTierService).
  // Unlimited tiers (pro / enterprise / paid) return -1 and render the
  // unlimited widget instead.
  const summaryLimit = userTierService.getTierLimits(userTier || 'free').summaries;
  const isMeteredTier = summaryLimit > 0;

  return (
    <div className="relative z-[100] w-full">
      <header
        className="sticky top-0 z-50 flex h-14 w-full items-center justify-between gap-4 border-b border-white/[0.06] bg-cg-nav bg-[var(--cg-nav)] px-4 sm:px-9"
        style={{ backgroundColor: 'var(--cg-nav)' }}
      >
        <div className="flex min-w-0 items-center gap-2">
          {!isAnonymous && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md bg-white/[0.10] text-white transition-colors hover:bg-white/[0.18] md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          <Logo
            size="sm"
            surface="dark"
            className="hover:scale-[1.02] transition-transform duration-200 [&_span:last-child]:text-[15px]"
          />
        </div>

        {!isAnonymous && !onFirm && (
          <nav className="hidden min-w-0 flex-1 justify-center md:flex">
            <div className="flex items-center gap-0.5">
              <NavLink
                to="/"
                className={() => topLinkClass({
                  isActive: location.pathname === '/' || location.pathname === '/home',
                })}
              >
                Home
              </NavLink>
              <NavLink
                to="/proceedings"
                className={() => topLinkClass({
                  isActive: location.pathname === '/proceedings' || location.pathname.startsWith('/proceedings/'),
                })}
              >
                Dockets
              </NavLink>
              <NavLink
                to="/watchlist"
                className={() => topLinkClass({
                  isActive: location.pathname === '/watchlist',
                })}
              >
                Watchlist
              </NavLink>
              {isFirmMember && (
                <NavLink
                  to="/firm"
                  className={() => topLinkClass({
                    isActive: location.pathname === '/firm',
                  })}
                >
                  Firm console
                </NavLink>
              )}
              <NavLink
                to="/trending"
                className={() => topLinkClass({
                  isActive: location.pathname === '/trending',
                })}
              >
                Trending
              </NavLink>
              <NavLink
                to="/comment-summaries"
                className={() => topLinkClass({
                  isActive:
                    location.pathname === '/comment-summaries' ||
                    location.pathname.startsWith('/comment-summaries/'),
                })}
              >
                Comment Summaries
              </NavLink>
              <div
                className="relative"
                onMouseEnter={openLibraryMenu}
                onMouseLeave={scheduleCloseLibraryMenu}
              >
                <NavLink
                  to="/library"
                  className={() => topLinkClass({ isActive: isLibraryRoute })}
                  onClick={() => setLibraryMenuOpen(false)}
                >
                  <span className="inline-flex items-center gap-1">
                    Library
                    <ChevronDown className="h-3 w-3 opacity-70" />
                  </span>
                </NavLink>
                {libraryMenuOpen && (
                  <div
                    className="absolute left-1/2 top-full z-50 mt-1 w-44 -translate-x-1/2 rounded-md border border-white/[0.10] bg-cg-nav/95 bg-[var(--cg-nav)]/95 py-1 shadow-xl backdrop-blur-md"
                    onMouseEnter={openLibraryMenu}
                    onMouseLeave={scheduleCloseLibraryMenu}
                  >
                    <NavLink
                      to="/library?tab=feed"
                      onClick={() => setLibraryMenuOpen(false)}
                      className={({ isActive: _ }) => {
                        const active =
                          isLibraryRoute &&
                          (new URLSearchParams(location.search).get('tab') ?? 'feed') === 'feed';
                        return `flex items-center gap-2 px-3 py-1.5 text-[13px] transition-colors ${
                          active ? 'bg-white/[0.08] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                        }`;
                      }}
                    >
                      <Rss className="h-3.5 w-3.5" />
                      Feed
                    </NavLink>
                    <NavLink
                      to="/library?tab=summaries"
                      onClick={() => setLibraryMenuOpen(false)}
                      className={() => {
                        const active = isSummariesTab;
                        return `flex items-center gap-2 px-3 py-1.5 text-[13px] transition-colors ${
                          active ? 'bg-white/[0.08] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                        }`;
                      }}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Summaries
                    </NavLink>
                    <NavLink
                      to="/library?tab=dockets"
                      onClick={() => setLibraryMenuOpen(false)}
                      className={() => {
                        const active =
                          isLibraryRoute &&
                          new URLSearchParams(location.search).get('tab') === 'dockets';
                        return `flex items-center gap-2 px-3 py-1.5 text-[13px] transition-colors ${
                          active ? 'bg-white/[0.08] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                        }`;
                      }}
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      My Dockets
                    </NavLink>
                  </div>
                )}
              </div>
              {canUsePublicFiles(userEmail) && (
                <NavLink
                  to="/public-files"
                  className={() => topLinkClass({
                    isActive: location.pathname === '/public-files' || location.pathname.startsWith('/public-files/'),
                  })}
                >
                  Public Files
                </NavLink>
              )}
              <NavLink
                to="/workspaces"
                className={() => topLinkClass({
                  isActive: location.pathname.startsWith('/workspace') || location.pathname === '/workspaces' || location.pathname === '/teams',
                })}
              >
                Workspaces
              </NavLink>
            </div>
          </nav>
        )}

        {/* Signed-out visitors still get a slim browse nav to the public
            surfaces — otherwise the whole bar was hidden and the only way onward
            from a shared link was to sign up, blunting the self-serve funnel. */}
        {isAnonymous && !onFirm && (
          <nav className="hidden min-w-0 flex-1 justify-center md:flex">
            <div className="flex items-center gap-0.5">
              <NavLink
                to="/proceedings"
                className={() => topLinkClass({
                  isActive: location.pathname === '/proceedings' || location.pathname.startsWith('/proceedings/'),
                })}
              >
                Dockets
              </NavLink>
              <NavLink
                to="/trending"
                className={() => topLinkClass({
                  isActive: location.pathname === '/trending',
                })}
              >
                Trending
              </NavLink>
            </div>
          </nav>
        )}

        <div className="flex shrink-0 items-center gap-3 sm:gap-5">
          {isAnonymous ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="hidden rounded-md px-3 py-1.5 text-[13px] text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white/90 sm:inline"
              >
                Pricing
              </button>
              <button
                type="button"
                onClick={onShowAuth}
                className="rounded-md px-3 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'var(--cg-green)' }}
              >
                Sign Up
              </button>
              <button
                type="button"
                onClick={onShowAuth}
                className="text-[13px] font-normal text-white/70 transition-colors hover:text-white"
              >
                Log In
              </button>
            </>
          ) : (
            <>
              {/* Only render the plan pill once the tier is actually resolved — never
                  flash "Free Plan" at a paid user while it loads (or on error). */}
              {!onFirm && userTier && !tierError && (
                <span
                  className="hidden rounded-full border px-2.5 py-0.5 text-[11px] font-medium tracking-wide sm:inline"
                  style={{
                    color: 'var(--cg-green)',
                    backgroundColor: 'color-mix(in srgb, var(--cg-green) 15%, transparent)',
                    borderColor: 'color-mix(in srgb, var(--cg-green) 30%, transparent)',
                  }}
                >
                  {getTierDisplayName(userTier)}
                </span>
              )}
              <div className="relative" ref={accountDropdownRef}>
                <button
                  type="button"
                  onClick={toggleAccountDropdown}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: 'var(--cg-green)' }}
                  title="Account"
                >
                  {avatarInitial}
                </button>
                     
                     {/* Account Dropdown */}
                     {accountDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-72 bg-white border border-green-200 rounded-lg shadow-lg z-50 overflow-hidden">
                        {/* Metered-tier layout (free = 2/mo, prosumer = 5/mo) */}
                        {isMeteredTier ? (
                          <div className="py-2">
                            <div className="px-4 py-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 truncate">
                                    {userEmail || 'Account'}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                                    {tierError ? (
                                      <span className="text-gray-400">Plan details unavailable</span>
                                    ) : userTier ? (
                                      <>
                                        <span>{getTierDisplayName(userTier)}</span>
                                        <Star className="w-3.5 h-3.5 text-yellow-500" />
                                      </>
                                    ) : (
                                      <span className="text-gray-400">Loading…</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {!tierError && (
                              <>
                                <div className="px-4 pb-3">
                                  <div className="text-xs text-gray-700 flex items-center justify-between">
                                    <span className="font-medium">Usage</span>
                                    <span className="text-gray-600">
                                      {commentSummariesUsage}/{summaryLimit} comment summaries this month
                                    </span>
                                  </div>
                                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="h-2 rounded-full bg-green-500 transition-all duration-300"
                                      style={{ width: `${Math.min((commentSummariesUsage / summaryLimit) * 100, 100)}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="px-4 pb-2">
                                  <button
                                    onClick={() => {
                                      navigate('/pricing');
                                      setAccountDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors"
                                  >
                                    <ArrowUpRight className="w-4 h-4" />
                                    <span>{userTier === 'prosumer' ? 'Upgrade to Pro' : 'Upgrade'}</span>
                                  </button>
                                </div>
                              </>
                            )}

                            <hr className="my-1 border-gray-200" />

                            <button
                              onClick={() => {
                                navigate('/account');
                                setAccountDropdownOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Settings className="w-4 h-4" />
                              <span>Account Settings</span>
                            </button>

                            <button
                              onClick={() => {
                                navigate('/support');
                                setAccountDropdownOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <HelpCircle className="w-4 h-4" />
                              <span>Help &amp; Support</span>
                            </button>

                            <hr className="my-1 border-gray-200" />

                            <button
                              onClick={() => {
                                onSignOut();
                                setAccountDropdownOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              <span>Sign Out</span>
                            </button>
                          </div>
                        ) : (
                          // Default layout for non-free tiers (unchanged)
                          <div className="py-2">
                            <button
                              onClick={() => {
                                navigate('/account');
                                setAccountDropdownOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Settings className="w-4 h-4" />
                              <span>Account Settings</span>
                            </button>
                            <hr className="my-1 border-gray-200" />
                            <button
                              onClick={() => {
                                onSignOut();
                                setAccountDropdownOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              <span>Sign Out</span>
                            </button>
                          </div>
                        )}
                      </div>
                     )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* Sidebar Overlay — only for logged-in users */}
      {!isAnonymous && sidebarOpen && (
        <div aria-hidden="true"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar — only for logged-in users */}
      {!isAnonymous && (
      <div
        className={`fixed top-0 left-0 z-50 flex h-full w-80 flex-col border-r border-cg-border bg-cg-cream shadow-xl transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header */}
        <div
          className="flex flex-shrink-0 items-center justify-between border-b border-white/10 bg-cg-nav bg-[var(--cg-nav)] px-5 py-4"
          style={{ backgroundColor: 'var(--cg-nav)' }}
        >
          <Logo
            size="sm"
            surface="dark"
            className="hover:scale-[1.02] transition-transform duration-200 [&_span:last-child]:text-[15px]"
            onClick={closeSidebar}
          />
          
          <button
            type="button"
            onClick={closeSidebar}
            className="rounded-md p-2 text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Navigation Items */}
          <nav className="space-y-2 p-5 pt-4">
            {navItems.map((item) => {
              const Icon = item.icon;

              const pathBase = item.path.split('?')[0];
              const isActive =
                item.label === 'Library'
                  ? location.pathname === '/library'
                  : item.label === 'Proceedings'
                      ? location.pathname === '/proceedings' ||
                        location.pathname.startsWith('/proceedings/')
                    : item.label === 'Workspaces'
                      ? location.pathname.startsWith('/workspace') ||
                        location.pathname === '/workspaces' ||
                        location.pathname === '/teams'
                    : pathBase === '/'
                      ? location.pathname === '/' || location.pathname === '/home'
                      : location.pathname === pathBase;
              const isPremiumFeature = (item as any).isPremium;
              const hasAccess = !isPremiumFeature || userTier === 'pro' || userTier === 'enterprise';
              
              const handleClick = (e: React.MouseEvent) => {
                if (isPremiumFeature && !hasAccess) {
                  e.preventDefault();
                  // Navigate to pricing page instead
                  window.location.href = '/pricing';
                  return;
                }
                closeSidebar();
              };
              
              return (
                <Link
                  key={item.path}
                  to={hasAccess ? item.path : '/pricing'}
                  onClick={handleClick}
                  className={`flex w-full items-center space-x-3 rounded-xl border px-4 py-3 transition-all duration-200 ${
                    isActive
                      ? 'border-cg-ink bg-cg-ink text-white'
                      : hasAccess
                        ? 'border-cg-border bg-white text-cg-ink hover:border-neutral-400'
                        : 'border-cg-border bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {isPremiumFeature && !hasAccess && (
                    <span className="ml-auto text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                      Pro
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Section - Fixed at Bottom */}
        <div className="flex-shrink-0 border-t border-cg-border bg-white p-6">
          {/* Simplified Usage Display */}
          {!isAnonymous && userEmail && (
            <div className="mb-4">
              {loading ? (
                // Loading state
                <div className="p-3 bg-white/90 border border-green-300/50 rounded-xl animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-full"></div>
                </div>
              ) : isMeteredTier ? (
                // Metered tier (free = 2/mo, prosumer = 5/mo): summaries progress bar
                <div className="p-3 bg-white/90 border border-green-300/50 rounded-xl">
                  <div className="flex justify-between text-xs text-gray-700 mb-2">
                    <span className="font-medium">Comment Summaries</span>
                    <span className="font-bold">{commentSummariesUsage}/{summaryLimit}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        commentSummariesUsage >= summaryLimit
                          ? 'bg-red-500'
                          : commentSummariesUsage >= summaryLimit * 0.8
                          ? 'bg-orange-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min((commentSummariesUsage / summaryLimit) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ) : userTier === 'pro' ? (
                // Pro tier: Show plan name and count
                <div className="p-3 bg-green-50 border border-green-300 rounded-xl">
                  <div className="flex items-center space-x-2 text-green-700">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium">Pro Plan</span>
                  </div>
                  <p className="text-green-600 text-xs mt-1">
                    {commentSummariesUsage} summaries this month
                  </p>
                </div>
              ) : userTier === 'enterprise' ? (
                // Enterprise tier: Show plan name and count
                <div className="p-3 bg-green-50 border border-green-300 rounded-xl">
                  <div className="flex items-center space-x-2 text-green-700">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium">Enterprise Plan</span>
                  </div>
                  <p className="text-green-600 text-xs mt-1">
                    {commentSummariesUsage} summaries this month
                  </p>
                </div>
              ) : null}
            </div>
          )}
          {userEmail && !isAnonymous && (
            <div className="flex items-center space-x-3 mb-4 p-3 rounded-xl bg-white/50 border border-green-200/30">
              <div className="p-2 rounded-lg bg-green-500/20 border border-green-400/30">
                <User className="w-4 h-4 text-green-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-black text-sm font-medium truncate" title={userEmail}>
                  {userEmail}
                </p>
                {userTier && (
                  <p className="text-green-600 text-xs font-medium">
                    {getTierDisplayName(userTier)}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {isAnonymous ? (
            <div className="space-y-2">
              <button
                onClick={() => {
                  onShowAuth?.();
                  closeSidebar();
                }}
                className="flex items-center justify-center space-x-3 w-full px-4 py-3 bg-green-500 hover:bg-green-600 border border-green-600 rounded-xl text-white font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <LogIn className="w-5 h-5" />
                <span>Sign Up</span>
              </button>
              <button
                onClick={() => {
                  onShowAuth?.();
                  closeSidebar();
                }}
                className="flex items-center justify-center space-x-3 w-full px-4 py-3 text-green-600 hover:text-green-800 font-medium transition-colors duration-200"
              >
                <span>Log In</span>
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Link
                to="/account"
                onClick={closeSidebar}
                className="flex items-center space-x-3 w-full px-4 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 rounded-xl text-blue-700 hover:text-blue-800 transition-colors duration-200 shadow-sm"
              >
                <User className="w-5 h-5" />
                <span className="font-medium">Account</span>
              </Link>
              <button
                onClick={() => {
                  onSignOut();
                  closeSidebar();
                }}
                className="flex items-center space-x-3 w-full px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 rounded-xl text-red-700 hover:text-red-800 transition-colors duration-200 shadow-sm"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      )} {/* end !isAnonymous sidebar */}
    </div>
  );
};